<i18n>
{
  "en": {
    "label": {
      "uploadFiles": "Upload",
      "selectFiles": "Select files / Drop files here",
      "onlyJpegPreview": "(jpg/jpeg are only previewable)",
      "maxUpload": "You can upload up to 3GB in total at one time",
      "hiResMaxUpload": "You can upload files up to 300 MB each and 1 GB in total at one time. (For high-resolution images, TIFF files may exceed the size limit, so JPEG format is recommended.)",
      "placeholderSelectType": "please select type",
      "userComment": "Please write here if there is a supplementary comment"
    },
    "message" : {
      "optionsUnselected": "Selection option for each files is needed.",
      "ngFileAlert": "The following file format files can not be uploaded.",
      "hiResUploadTypeNgFileAlert": "The following files cannot be uploaded because they are not JPEG or TIFF data. (Even if the extension is jpg, you cannot upload if the file format is not JPEG. Please check the file format.)",
      "aboutDataType": "about data type"
    }
  },
  "ja": {
    "label": {
      "uploadFiles": "アップロード",
      "selectFiles": "ファイルを選択／ここにドロップ",
      "onlyJpegPreview": "※jpg/jpeg のみプレビューされます",
      "maxUpload": "※1ファイル300MB、合計１GBまで1度にアップロードできます",
      "hiResMaxUpload": "※1ファイル300MB、合計1GBまで1度にアップロードできます（高画素化された場合、TIFF形式は上限超過の可能性があるため、JPEG形式を推奨）",
      "placeholderSelectType": "データの種類を選択",
      "userComment": "補足コメントがあればこちらにお書き下さい"
    },
    "message" : {
      "optionsUnselected": "データの種類が未選択のものがあります",
      "ngFileAlert": "次のファイル形式のファイルはアップロードできません。",
      "hiResUploadTypeNgFileAlert": "次のファイルは、JPEGもしくはTIFFデータでなかったためアップロードできません。(拡張子がjpgでも、ファイル形式がJPEGでない場合はアップロードできません。ファイル形式をご確認ください)",
      "aboutDataType": "データの種類とは"
    }
  }
}
</i18n>

<template>
  <v-container>
    <div class="filedrop-wrapper">
      <div class="helper"></div>
      <div
        class="drop display-inline align-center"
        :class="{ 'drop-dragging': isDragging }"
        @dragover="onDragOver"
        @dragenter="onDragIn"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <!-- <div class="hover-layer"></div>  -->
        <div class="helper"></div>
        <label class="btn">
          {{ $t('label.selectFiles') }}
          <input
            type="file"
            multiple
            name="image"
            @change="onChange"
            ref="fileInput"
          />
        </label>
        <br />
        <!--<p>{{ $t('label.onlyJpegPreview') }}</p>-->
        <p :class="{ 'hi-res-max-upload': isHiResUpload }">
          {{ $t(maxUploadLabel) }}
        </p>
      </div>
      <br />
      <br />

      <div>
        <div
          v-for="(item, index) in items"
          :key="index"
          avatar
          @click.prevent
          style="display: flex; justify-content: center;"
        >
          <div style="display: flex; flex-direction: row; justify-content: space-between; width: 100%; max-width: 640px; align-items: center;">
            <v-avatar
              :tile="true"
              :size="64"
              class="hidden-sm-and-down"
            >
              <img
                v-if="item.localUrl"
                :src="item.localUrl"
                style="width: auto; max-width: 64px; height: auto; max-height: 64px; "
              />
            </v-avatar>

            <div>
              <div style="max-width: 640px; word-wrap: break-word;">{{ item.name }}（{{ formatFileSize(item.size) }}）</div>
              <!-- // TODO textJa -->
              <v-select
                v-if="_subOption.length > 0"
                v-model="subOptionAnswers[item.uid]"
                dense
                :items="_subOption"
                item-text="textJa"
                item-value="index"
                label
                @input="subOptionInput(item.uid)"
                :placeholder="$t('label.placeholderSelectType')"
              ></v-select>
            </div>

            <div>
              <v-btn icon @click="removeFile(index)">
                <v-icon>cancel</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <div v-if="items.length > 0">
      {{ $t('message.aboutDataType') }}<a :href="'/cp/' + getCampaignCode() + '/me/help#category2'" target="_blank" style="text-decoration: none;"><v-icon class="ml-1 hover-icon" slot="activator" dark color="primary">help_outline</v-icon></a>
      </div>
      <br />
      <TextAreaInput
        v-if="isVisibleUserComment"
        name="userComment"
        v-model="userComment"
        :placeholderValue="this.$t('label.userComment')"
      >
      </TextAreaInput>
      <v-btn
        :disabled="loading"
        color="primary"
        @click="onUpload"
        v-if="items.length > 0"
      >
        {{ $t('label.uploadFiles') }}
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import TextAreaInput from '@/components/atoms/TextAreaInput.vue';
import { getUniqueStr, convertByteSize } from '@/utils';
import StorageManager from '@/utils/StorageManager';
import { EnumSubmissionType } from '@/enum/enum.js';
import FileType from 'file-type';

/**
 * FileDropComponent
 *
 * - ファイルには全てuidを付与して返却
 * - 返却する値は Array
 * - Array内に `uid` や `size` などの情報を持ったObjectを包含
 * - subOptionが指定された場合は加えて `subOptionAnswer` を付与
 */
export default {
  components: {
    TextAreaInput
  },
  data() {
    return {
      items: [],
      userComment: '',
      isDragging: false,
      isInnerDragMove: false,
      subOptionAnswers: [],
      loading: false
    };
  },
  props: {
    /**
     * ファイルごとの選択肢を付与するオプション
     * 指定された場合、 `subOptionAnswer` をkeyとした情報を付与する
     */
    subOption: {
      type: Array,
      default: () => []
    },
    mustAnsToSubOption: {
      type: Boolean,
      default: false
    },
    submissionType: {
      type: Number,
      default: null
    },
    /*
     * **Promiseを返却するFuncであることが必須**
     */
    uploadFunc: {
      type: Function,
      default: () => {
        return new Promise();
      }
    },
    clearOnUpload: {
      type: Boolean,
      default: true
    },
    isVisibleUserComment: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    isHiResUpload() {
      return this.submissionType === EnumSubmissionType.HiRes;
    },
    maxUploadLabel() {
      return this.isHiResUpload ? 'label.hiResMaxUpload' : 'label.maxUpload';
    },
    _subOption: function() {
      return this.subOption.map((value, index) => {
        return { index, ...value };
      });
    }
  },
  methods: {
    onDragOver(e) {
      this.isInnerDragMove = false; // 子要素間移動の場合は発生しないはずなので
      e.preventDefault();
    },
    onDragIn() {
      this.isDragging = true;
      this.isInnerDragMove = true;
    },
    onDragLeave() {
      if (!this.isInnerDragMove) {
        this.isDragging = false;
      }
    },
    onDrop(e) {
      // ドラッグ動作系のフラグ初期化
      this.isDragging = false;
      this.isInnerDragMove = false;
      // default eventの停止
      e.stopPropagation();
      e.preventDefault();

      const files = e.dataTransfer.files;
      this.addFilesToItems(files);
    },
    onChange(e) {
      const files = e.target.files;
      this.addFilesToItems(files);
      // 選択→消す→同じファイルを選択 されたとき,
      // inputがクリアされてないとonchangeなどのイベントが発動しない
      // が、しかし、iOS系は、DOM内のinput.typeを直接いじるとブラウザが落ちてしまうため、
      // iOS系の場合は、以下の処理を飛ばす
      const isIos = /iP(hone|(o|a)d)/.test(navigator.userAgent);
      if (!isIos) {
        this.resetFileInput();
      }
    },
    resetFileInput() {
      const input = this.$refs.fileInput;
      input.type = 'text';
      input.type = 'file';
    },
    removeFile(index) {
      this.items = this.items.filter((elem, num) => num !== index);
    },
    /**
     * sub optionが選択されたとき、`items` の該当のデータに `subOptionAnswer` としてvalueを付与
     * @param String uid 該当のitemのuid
     */
    subOptionInput(uid) {
      // TODO イケてないけど他に良さげな方法がない。itemsをきれいに保つことを優先
      for (const file of this.items) {
        // _subOptionによってindexがvalueになっていることが保証されている
        if (file.uid === uid)
          file.subOptionAnswer = this.subOption[this.subOptionAnswers[uid]];
      }
    },
    async addFilesToItems(files) {
      const previewableTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const hiResUploadTypes = ['image/jpeg', 'image/tiff']; // 高解像度データアップロードで許可するMIMEタイプを定義
      const ngTypes = ['application/zip']; //取り扱えないファイルのcontentTypeをここに記載する
      let ngFiles = '';
      let hiResUploadTypeNgFiles = '';
      for (let i = 0; i < files.length; i++) {
        //ファイルがNGファイルかどうかをチェック
        if (ngTypes.includes(files[i].type)) {
          ngFiles += files[i].name + '\r\n';
          //NGだった場合ファイル追加処理を飛ばす
          continue;
        }

        // 高解像度データアップロード時のファイルチェック
        // 高解像度データはJPEG、TIFFファイルがアップロード可能
        // それ以外のファイルがアップロードされた場合に、フォトスポットシステム転送処理でエラーになってしまうため
        // 拡張子ではなく、ファイルの中身を確認して判定を行う
        if (this.submissionType === EnumSubmissionType.HiRes) {
          // MIMEタイプの取得
          const buffer = await files[i].arrayBuffer()
          const fileType = await FileType.fromBuffer(buffer)
          
          // エラー判定
          if (fileType === undefined) {
            // ファイルタイプが特定できなかった場合の処理
            hiResUploadTypeNgFiles += files[i].name + '(ファイル形式: 不明)' + '\r\n';
            //NGだった場合ファイル追加処理を飛ばす
            continue;
          } else if (!hiResUploadTypes.includes(fileType.mime)) {
            // ファイルタイプがJPEG、または、TIFFでなかった場合の処理
            hiResUploadTypeNgFiles += files[i].name + `(ファイル形式: ${fileType.mime})` + '\r\n';
            //NGだった場合ファイル追加処理を飛ばす
            continue;
          }
        }

        const uid = getUniqueStr(files[i].name);
        let localUrl;
        // FileListは拡張するか`for length`を用いる必要がある
        if (previewableTypes.includes(files[i].type)) {
          localUrl = window.URL.createObjectURL(files[i]);
        }
        this.items.push({
          name: files[i].name,
          size: files[i].size,
          uid: uid,
          localUrl: localUrl,
          dataAsForm: files[i]
        });
        // SubOptionの選択肢が1つの場合はデフォルトで選択させる
        if (this.subOption.length === 1) {
          for (const file of this.items) {
            if (file.uid === uid) {
              this.subOptionAnswers[uid] = this._subOption[0];
              file.subOptionAnswer = this._subOption[0];
            }
          }
        }
      }
      //NGファイルがあった場合警告をだす
      if (ngFiles != '') {
        alert(this.$t('message.ngFileAlert') + '\r\n\r\n' + ngFiles);
      }
      if (hiResUploadTypeNgFiles != '') {
        alert(this.$t('message.hiResUploadTypeNgFileAlert') + '\r\n\r\n' + hiResUploadTypeNgFiles);
      }
    },
    formatFileSize(sizeStr) {
      return convertByteSize(sizeStr);
    },
    onUpload() {
      if (this.mustAnsToSubOption) {
        for (let item of this.items) {
          if (!item.subOptionAnswer) {
            alert(this.$t('message.optionsUnselected'));
            return;
          }
        }
      }
      this.loading = true;
      this.uploadFunc(this.items, this.userComment)
        .then(() => {
          if (this.clearOnUpload) {
            this.clearFiles();
          }
        })
        .catch(() => {
          // do nothing
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearFiles() {
      this.items = [];
    },
    getCampaignCode: () => {
      return StorageManager.getItem('CAMPAIGN_CODE_SESSION_KEY');
    }
  },
  watch: {
    submissionType: function(newValue, oldValue) {
      // アップロードダイアログを閉じた際に、アップロード中のデータが存在する場合に
      // 別タイプのファイルが表示されることを防ぐため
      // 登録データタイプ(高解像度、本人確認)が切り替わった場合は、登録済みファイルをクリアする
      if(newValue !== oldValue) {
        this.clearFiles();
      }
    }
  }
};
</script>

<style lang="stylus" scoped>
*, *:after, *:before {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-touch-callout: none;
}

.filedrop-wrapper {
  height: 100%;
  min-height: 100px;
  text-align: center;
}

.btn {
  background-color: #55355e;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: block;
  font-weight: bold;
  padding: 15px 35px;
  position: relative;
  width: 90%;
  margin: 0 auto;
  border-radius: 2px;
  box-shadow:0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)
}

.btn:hover {
  opacity: 0.9;
}

.hi-res-max-upload {
  margin-left: 5%;
  margin-right: 5%;
}

input[type='file'] {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.align-center {
  text-align: center;
}

.helper {
  height: 100%;
  display: inline-block;
  vertical-align: middle;
  width: 0;
}

.hidden {
  display: none !important;
}

.hidden.image {
  display: inline-block !important;
}

.display-inline {
  display: inline-block;
  vertical-align: middle;
}

.img {
  border: 1px solid #f6f6f6;
  display: inline-block;
  height: auto;
  max-height: 80%;
  max-width: 80%;
  width: auto;
}

.drop {
  position: relative;
  background-color: #f2f2f2;
  border: 4px dashed #ccc;
  background-color: #f6f6f6;
  border-radius: 2px;
  height: 100%;
  min-height: 100px;
  max-height: 400px;
  max-width: 600px;
  width: 100%;
  text-align: center;
}

.drop-dragging {
  opacity: 0.6;
}

div.v-input.v-text-field.v-text-field--placeholder {
  padding-top: 0;
}
</style>
